'use client';
/* eslint-disable jsx-a11y/no-redundant-roles, jsx-a11y/no-interactive-element-to-noninteractive-role --
   The table is laid out with `display: grid` (CMS-1772 R2), and Chrome and
   WebKit drop the native table semantics whenever `display` is overridden. The
   roles below look redundant against the markup but are what keeps the table in
   the accessibility tree, so they must stay. */

import classNames from 'classnames';
import { useState } from 'react';
import { PushDataLayerFn } from '../../../types/pushDataLayer';
import { useCmsClientContext } from '../../../utils/cmsPageContext';
import { getClientTranslations } from '../../../utils/translations/getClientTranslations';
import { CELL_PADDING, HOT_REWARD_CELL_BG } from '../constants';
import { MAX_HOT_REWARD_GIFTS } from '../hotReward';
import { TRANSLATIONS } from '../i18n';
import { HotRewardCellData } from '../types';
import { HotRewardModule } from './HotRewardModule';

interface HotRewardCellProps {
  data: HotRewardCellData;
  pushDataLayer?: PushDataLayerFn;
  index: number;
  titleTableBuilder: string;
}

/**
 * Hot Reward Pick value cell (CMS-1687).
 *
 * Renders up to 2 gift titles as stacked lines (no bullets, never truncated).
 * Shows `-` when there are none. The "See the full reward" link is a modal
 * trigger (a `<button>`, since it opens client-side UI rather than navigating)
 * and only renders when at least one gift title is shown.
 *
 * The titles are the authored value-cell gift selector (`data.giftTitles`),
 * pre-mapped server-side, so rendering is a pure, date-free pass (SSR-safe, no
 * CLS).
 */
export const HotRewardCell = ({
  data,
  pushDataLayer,
  index,
  titleTableBuilder,
}: HotRewardCellProps) => {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(TRANSLATIONS, context);
  const [isModuleOpen, setIsModuleOpen] = useState(false);

  const gifts = data.giftTitles.slice(0, MAX_HOT_REWARD_GIFTS);
  const hasGifts = gifts.length > 0;

  return (
    <td
      role="cell"
      className={classNames(
        HOT_REWARD_CELL_BG.get(context.brand),
        'border-l border-t border-borders/default',
        'min-h-table/cell-min-height',
        CELL_PADDING,
        // Reward content is never truncated: it wraps and the row grows to fit,
        // and the column width is unaffected either way (CMS-1772 R3).
        'break-words',
      )}
    >
      <div className="flex h-full flex-col justify-center gap-spacing/gap/y">
        {hasGifts ? (
          <div className="flex flex-col gap-spacing/padding/y-sm">
            {gifts.map(({ id, title }, i) => (
              <span
                // eslint-disable-next-line react/no-array-index-key -- override titles can share a fallback id (0); index disambiguates
                key={`${id}-${i}`}
                className={classNames(
                  'font-font-weight/table-cell-normal text-font-size/table-cell leading-line-height/table-cell',
                  'text-text/accent',
                )}
              >
                {title}
              </span>
            ))}
          </div>
        ) : (
          <span className="font-font-weight/table-cell-normal text-font-size/table-cell leading-line-height/table-cell text-text/primary">
            -
          </span>
        )}
        {hasGifts && (
          <button
            type="button"
            onClick={() => setIsModuleOpen(true)}
            className={classNames(
              'text-left cursor-pointer',
              'text-buttons/primary underline',
              'font-font-weight/table-cell-strong text-font-size/table-cell leading-line-height/table-cell',
            )}
          >
            {staticText.see_full_reward}
          </button>
        )}
      </div>
      {hasGifts && isModuleOpen && (
        <HotRewardModule
          data={data}
          isOpen={isModuleOpen}
          onClose={() => setIsModuleOpen(false)}
          pushDataLayer={pushDataLayer}
          index={index}
          titleTableBuilder={titleTableBuilder}
        />
      )}
    </td>
  );
};
